"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ClipboardCheck, HardHat, CircleCheck as CheckCircle2, ArrowRight, Shield, FileCheck2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface AccreditationItem {
  id: string;
  name: string;
  code: string;
  tag: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  features: string;
}
const accreditations: AccreditationItem[] = [{
  id: "nasc",
  name: "NASC",
  code: "National Access & Scaffolding Confederation",
  tag: "Industry-Recognised Standard",
  description: "Full compliance with TG20:21 operational design criteria and SG4:22 fall prevention guidelines on all structural works.",
  icon: ShieldCheck,
  features: "TG20:21 compliance & audited procedures"
}, {
  id: "chas",
  name: "CHAS",
  code: "Contractors Health & Safety Assessment",
  tag: "Verified Contractor",
  description: "Audited health, safety, and risk management systems demonstrating strict operational standards for commercial sites.",
  icon: FileCheck2,
  features: "Audited health & safety standards"
}];
const safetyMetrics = [{
  label: "Inspection Protocol",
  value: "7-Day Audits",
  detail: "Handover certificates and weekly statutory re-inspections."
}, {
  label: "Workforce Competence",
  value: "Trained and competent scaffolding operatives",
  detail: "Our operatives hold industry qualifications."
}, {
  label: "Site Safety Standard",
  value: "Full PPE Standard",
  detail: "Tethered tools, harness attachment, and hard hat enforcement."
}];
export default function Accreditations() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="trust-accreditations-safety" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Structural Datum Rule Line */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-1 w-12 bg-primary rounded-lg" />
          <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Verified Standards & Compliance
          </span>
        </div>

        {/* Section Header & Safety Commitment Statement */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16">
          <div className="lg:col-span-7">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
              Safety standards built into every scaffold.
            </h2>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-between">
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              We inspect every structure before handover, re-check weekly, and keep your site compliant from day one. Backed by Pete Wood&apos;s 20+ years of frontline experience.
            </p>
          </div>
        </div>

        {/* Accreditations Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {accreditations.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card rounded-lg shadow-xs hover:border-primary/60 transition-[border-color,box-shadow] duration-200 flex flex-col justify-between">
                  <div>
                    <CardHeader className="p-6 pb-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center justify-center size-12 rounded-md bg-secondary text-secondary-foreground shadow-xs">
                          <IconComponent className="size-6 text-primary" />
                        </div>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-sm bg-accent text-accent-foreground border border-border">
                          <CheckCircle2 className="size-3 text-primary" />
                          {item.tag}
                        </span>
                      </div>
                      <CardTitle className="text-2xl font-bold text-foreground">
                        {item.name}
                      </CardTitle>
                      <p className="text-xs font-medium text-muted-foreground mt-1">
                        {item.code}
                      </p>
                    </CardHeader>
                    <CardContent className="px-6 pb-6 pt-0">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardContent>
                  </div>
                  
                  <div className="px-6 pb-6 pt-0 mt-auto">
                    <div className="pt-4 border-t border-border flex items-center gap-2 text-xs font-medium text-foreground">
                      <Shield className="size-4 text-primary shrink-0" />
                      <span>{item.features}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Safety Specification Metric Band */}
        <div className="bg-muted/60 border border-border rounded-lg p-6 sm:p-8 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-border">
            {safetyMetrics.map((metric, i) => <div key={metric.label} data-index={i} className={`flex flex-col justify-between ${i !== 0 ? "pt-6 md:pt-0 md:pl-8" : ""}`}>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {i === 0 && <ClipboardCheck className="size-4 text-primary shrink-0" />}
                    {i === 1 && <HardHat className="size-4 text-primary shrink-0" />}
                    {i === 2 && <ShieldCheck className="size-4 text-primary shrink-0" />}
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {metric.label}
                    </span>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-foreground mb-1">
                    {metric.value}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {metric.detail}
                </p>
              </div>)}
          </div>
        </div>

        {/* Action Button & Contact Link */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6 bg-card border border-border rounded-lg shadow-xs">
          <div className="text-center sm:text-left">
            <h3 className="text-base font-semibold text-foreground">
              Need comprehensive safety documentation for your site?
            </h3>
            <p className="text-sm text-muted-foreground">
              Review our full safety policy, method statements, and risk assessment standards.
            </p>
          </div>
          <Button asChild variant="secondary" className="w-full sm:w-auto h-11 px-6 shrink-0">
            <Link to="/HealthSafetyAccreditationsPage" className="inline-flex items-center gap-2">
              <span>View Health & Safety Details</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}